import DOMPurify from 'dompurify';
import { replaceHtmlString } from '@winging/util/helper/messageReplaceNew';

/**
 * 채팅 HTML 최종 방어선 (plan/chat/chat-redesign.md S1).
 *
 * 삽입 위치 원칙:
 * - messageReplaceNew 파이프라인 "내부"가 아니라 그 출력(경계)에만 적용한다.
 *   파이프라인은 골든 마스터 118 테스트로 동결되어 있어 내부 수정은 안전망을 파괴한다.
 * - 멱등이므로 저장 경계(messageFormatter)와 렌더 경계(dangerouslySetInnerHTML 콜사이트)에
 *   중복 적용해도 무해하다.
 *
 * 허용 정책은 골든 corpus 5,050건의 실측 인벤토리에서 유도했다:
 * - 태그: 파이프라인이 실제 산출하는 태그 + 무해한 서식 태그.
 * - iframe: 파이프라인이 의도 생성하는 유튜브 embed만 허용(그 외 전부 제거).
 * - 속성: 표준 허용셋 + data-* + 레거시 messageReplace 커스텀 속성만 보존.
 * - URI: https/http/mailto/tel 스킴과 스킴 없는 상대 경로만 허용.
 */

const YOUTUBE_EMBED_PREFIX = 'https://www.youtube.com/embed/';

const ALLOWED_TAGS = [
  'a',
  'b',
  'blockquote',
  // body: 메시지 태그가 아니라 DOMPurify 내부 파싱 골격. fragment 파싱 시 문서 body를
  // 순회하는데 allowlist에 없으면 골격 제거가 removed에 기록돼 모든 마크업 입력이
  // '마크업 제거됨' 오탐을 낸다. 출력은 body.innerHTML 직렬화라 허용해도 결과 불변이고,
  // 리터럴 <body> 태그는 파서 단계에서 이미 병합·소거된다(속성도 on* 검사를 그대로 거침).
  'body',
  'br',
  'calendar-action',
  'code',
  'div',
  'em',
  'hr',
  'i',
  'iframe',
  'img',
  'li',
  'ol',
  'p',
  'pre',
  's',
  'source',
  'span',
  'strong',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
  'video',
];

/** 허용 스킴(https/http/mailto/tel) 또는 스킴 없는 값(상대 경로, #앵커)만 통과 */
const ALLOWED_URI_REGEXP =
  /^(?:(?:https?|mailto|tel):|(?![a-z][a-z0-9+.-]*:))/i;

/** 채팅 HTML에서 의미가 없거나 UI/보안 부작용 가능성이 있어 항상 제거할 속성 */
const FORBIDDEN_ATTRS = [
  'autofocus',
  'contenteditable',
  'popover',
  'srcset',
  'style',
];

/**
 * 커스텀 속성 보존 예외에서도 절대 강제 보존하지 않는 속성.
 * (URI/문서 주입 운반 가능)
 */
const FORCE_KEEP_DENYLIST = new Set([
  'action',
  'background',
  'formaction',
  'is',
  'ping',
  'poster',
  'srcdoc',
  'xlink:href',
  'xmlns',
]);

/** DOMPurify 기본 허용셋 밖이지만 채팅 렌더 파이프라인에서 실제 사용하는 속성 */
const EXTRA_ALLOWED_ATTRS = new Set([
  'allow',
  'allowfullscreen',
  'aria-label',
  'frameborder',
  'linktype',
  'searchdeptcode',
  'target',
]);

type ChatPurifier = ReturnType<typeof DOMPurify>;

let purifier: ChatPurifier | null = null;

/**
 * 채팅 전용 DOMPurify 인스턴스를 지연 생성한다.
 * 기본 export 인스턴스를 쓰지 않는 이유: hook이 전역이라 다른 소비자
 * (features/data TableNode)의 sanitize 동작까지 오염시키기 때문.
 */
const getPurifier = (): ChatPurifier | null => {
  if (purifier) {
    return purifier;
  }
  if (typeof window === 'undefined') {
    return null;
  }

  const instance = DOMPurify(window);

  instance.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName !== 'iframe') {
      return;
    }
    const src = (node as Element).getAttribute?.('src') ?? '';
    if (!src.startsWith(YOUTUBE_EMBED_PREFIX)) {
      node.parentNode?.removeChild(node);
    }
  });

  instance.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName === 'style') {
      // style은 값 검사가 없어 CSS 인젝션(전면 오버레이 클릭재킹, background-url
      // 유출) 벡터가 된다 — FORBID_ATTR로 기본 차단하되, 파이프라인이 의도 생성하는
      // 유튜브 embed iframe의 aspect-ratio 스타일만 보존한다(iframe src는 위
      // uponSanitizeElement에서 유튜브 embed로 이미 한정됨).
      if (String((node as Element).nodeName || '').toLowerCase() === 'iframe') {
        data.forceKeepAttr = true;
      }
      return;
    }
    if (FORBIDDEN_ATTRS.includes(data.attrName)) {
      return;
    }
    if (data.allowedAttributes[data.attrName]) {
      // 표준 허용 속성은 DOMPurify 기본 파이프라인(URI 검사 포함)에 맡긴다.
      return;
    }
    if (/^on/i.test(data.attrName)) {
      return;
    }
    if (FORCE_KEEP_DENYLIST.has(data.attrName)) {
      return;
    }
    if (EXTRA_ALLOWED_ATTRS.has(data.attrName)) {
      data.forceKeepAttr = true;
    }
  });

  purifier = instance;
  return purifier;
};

/**
 * 제거 이벤트 요약: 무엇이 제거됐는지(태그/속성 이름)를 진단 가능한 형태로 만든다.
 * DOMPurify.removed 항목은 { element } 또는 { attribute, from } 형태다.
 */
const summarizeRemoved = (
  removed: ReturnType<typeof DOMPurify>['removed']
): string[] =>
  removed.map((entry) => {
    if ('element' in entry && entry.element) {
      return `<${String((entry.element as Element).nodeName || 'node').toLowerCase()}>`;
    }
    if ('attribute' in entry && entry.attribute) {
      const from = (entry as unknown as { from?: Element }).from;
      const fromTag = from?.nodeName
        ? String(from.nodeName).toLowerCase()
        : '?';
      return `@${(entry.attribute as Attr).name} (on <${fromTag}>)`;
    }
    return 'unknown';
  });

/**
 * 제거 보고 dedupe: 같은 제거 패턴은 세션(페이지 수명)당 1회만 보고한다.
 * - 콜사이트가 렌더마다 재-sanitize해도 브라우저 경고 콘솔이 도배되지 않도록 한다.
 * - 이 경고는 Sentry 수집 대상이 아니며, 현장 디버깅을 위한 세션 단위 관측용이다.
 * - 상한 도달 시 보고를 멈추는 대신 가장 오래된 패턴을 밀어낸다(FIFO) — 벤인 패턴이
 *   50개 쌓인 뒤 유입되는 진짜 공격 신호가 침묵하지 않도록 한다(메모리도 상한 유지).
 */
const reportedRemovalPatterns = new Set<string>();
const MAX_REPORTED_REMOVAL_PATTERNS = 50;

/**
 * sanitize 결과 LRU 캐시.
 * - 콜사이트는 lint 규칙(S4) 때문에 __html에 sanitize 호출식을 직접 쓴다. 렌더마다
 *   반복 호출돼도 같은 입력은 DOMPurify를 다시 태우지 않도록 여기서 memoize한다.
 * - 채팅 메시지 문자열이 key/value라 항목 수 상한으로 메모리를 제한한다.
 */
const createLruCache = (maxEntries: number) => {
  const cache = new Map<string, string>();
  return {
    get(key: string): string | undefined {
      const value = cache.get(key);
      if (value !== undefined) {
        // 최근 사용으로 갱신 (Map 삽입 순서 = LRU 순서)
        cache.delete(key);
        cache.set(key, value);
      }
      return value;
    },
    set(key: string, value: string): void {
      if (cache.has(key)) {
        cache.delete(key);
      }
      cache.set(key, value);
      if (cache.size > maxEntries) {
        cache.delete(cache.keys().next().value as string);
      }
    },
  };
};

const sanitizeCache = createLruCache(200);
const replaceSafeCache = createLruCache(200);

/**
 * 채팅 메시지 HTML을 sanitize한다. 멱등.
 *
 * 마크업이 있는 입력이 SSR 경로에 들어오면 fail-closed('')로 동작한다 — 검증 없이
 * 원본을 돌려보내는 것(fail-open)은 조용한 XSS 우회가 되므로 금지. 현재 콜사이트는
 * 전부 클라이언트 렌더 경로이며, 'use client' 컴포넌트의 SSR 프리렌더 시점에는
 * 채팅 상태가 비어 있어 마크업 입력이 발생하지 않는다.
 */
export const sanitizeChatHtml = (html: string): string => {
  // 태그가 없는 입력은 마크업 공격면이 없다(innerHTML로 넣어도 텍스트/엔티티로만 해석).
  // 일반 텍스트 답변의 DOMPurify 비용을 피하고, SSR의 빈 문자열/평문 호출을 무해하게 통과시킨다.
  if (!html.includes('<')) {
    return html;
  }

  const cached = sanitizeCache.get(html);
  if (cached !== undefined) {
    return cached;
  }

  const instance = getPurifier();
  if (!instance) {
    console.error(
      new Error(
        'sanitizeChatHtml: DOM 없는 환경에서 호출됨 — 빈 문자열로 fail-closed'
      )
    );
    return '';
  }

  const clean = instance.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_URI_REGEXP,
    ALLOW_DATA_ATTR: true,
    // style은 iframe(유튜브 embed)에만 hook으로 예외 보존하고 그 외 전부 차단한다.
    FORBID_ATTR: FORBIDDEN_ATTRS,
  });

  // 개발 환경에서 정상 답변이 훼손되는지(또는 공격이 실제 유입되는지) 확인하기 위한
  // 경고다. 정상적인 보안 정제 결과를 Sentry 오류로 수집하지 않도록 error를 쓰지 않고,
  // 사용자 입력이나 답변 원문도 로그에 포함하지 않는다.
  // 같은 제거 패턴은 세션당 1회만 보고한다(렌더마다 재-sanitize하는 콜사이트의 도배 방지).
  if (instance.removed.length > 0) {
    const removed = summarizeRemoved(instance.removed);
    const pattern = removed.join(', ');
    if (!reportedRemovalPatterns.has(pattern)) {
      if (reportedRemovalPatterns.size >= MAX_REPORTED_REMOVAL_PATTERNS) {
        reportedRemovalPatterns.delete(
          reportedRemovalPatterns.values().next().value as string
        );
      }
      reportedRemovalPatterns.add(pattern);
      console.warn(new Error('sanitizeChatHtml: 마크업 제거됨'), {
        removedCount: instance.removed.length,
        removed,
      });
    }
  }

  sanitizeCache.set(html, clean);
  return clean;
};

/**
 * 원문 → HTML 변환(replaceHtmlString) + sanitize 합성.
 * 신규 콜사이트는 replaceHtmlString 대신 반드시 이것을 사용한다.
 * 변환 파이프라인(정규식 20단)도 렌더마다 반복되지 않도록 원문 기준으로 캐시한다.
 */
export const replaceHtmlStringSafe = (raw: string): string => {
  const cached = replaceSafeCache.get(raw);
  if (cached !== undefined) {
    return cached;
  }

  const safe = sanitizeChatHtml(replaceHtmlString(raw));
  replaceSafeCache.set(raw, safe);
  return safe;
};
